import Base from './Base';
import CaseStatus from './CaseStatus';
import { UPLOAD_OPTIONS } from '../constants/general';

export const caseColors = {
  archived: '#999999',
  completed: '#20794F',
  delayed: '#B12A2A',
  'in-progress': '#E2A74F',
  'in-review': '#999999',
  incomplete: '#0254A4',
  new: '#8BC217',
  'not-paid': '#B12A2A',
  'pending-images': '#4BACB8',
  'pending-upload': '#8BC217',
  review: '#787FF6',
  'upload-shared': '#8BC217',
  uploading: '#E2A74F',
};

export const CASE_STATUSES = {
  ARCHIVED: 'archived',
  COMPLETED: 'completed',
  DELAYED: 'delayed',
  INCOMPLETE: 'incomplete',
  IN_PROGRESS: 'in-progress',
  IN_REVIEW: 'in-review',
  NEW: 'new',
  NOT_PAID: 'not-paid',
  PENDING_IMAGES: 'pending-images',
  PENDING_UPLOAD: 'pending-upload',
  REVIEW: 'review',
  UPLOADING: 'uploading',
  UPLOAD_SHARED: 'upload-shared',
};

export const caseStatuses = {
  archived: new CaseStatus({ label: 'Archived', value: 'archived' }),
  completed: new CaseStatus({ label: 'Completed', value: 'completed' }),
  delayed: new CaseStatus({ label: 'Delayed', value: 'delayed' }),
  'in-progress': new CaseStatus({ label: 'In Progress', value: 'in-progress' }),
  'in-review': new CaseStatus({ label: 'In Review', value: 'in-review' }),
  incomplete: new CaseStatus({ label: 'Incomplete', value: 'incomplete' }),
  new: new CaseStatus({ label: 'New', value: 'new' }),
  'not-paid': new CaseStatus({ label: 'Not Paid', value: 'not-paid' }),
  'pending-images': new CaseStatus({ label: 'Pending Images', value: 'pending-images' }),
  'pending-upload': new CaseStatus({ label: 'Pending Upload', value: 'pending-upload' }),
  review: new CaseStatus({ label: 'Review', value: 'review' }),
  'upload-shared': new CaseStatus({ label: 'Pending Shared', value: 'upload-shared' }),
  uploading: new CaseStatus({ label: 'Uploading', value: 'uploading' }),
};

export const defaultCase = ['pending-upload', 'pending-images', 'upload-shared', 'new', 'delayed', 'uploading', 'in-progress', 'review', 'in-review', 'not-paid'];

export const adminCaseUploadOptions = {
  imageUpload: {
    label: "I will upload my patient's files",
    value: 'image-upload',
  },

  onlinePortal: {
    label: 'Access images via online portal',
    value: 'online-portal',
  },

  shareFolderLink: {
    label: 'Get MRI images from my online folder (Google Drive/Dropbox/other)',
    value: UPLOAD_OPTIONS.FOLDER_SHARE_LINK,
  },
};

export const lawFirmCaseUploadOptions = {
  ...adminCaseUploadOptions,

  mailToPOBox: {
    label: 'I would like to mail a disc to Mediphany',
    value: 'po-box-mail',
  },
};

export const allCaseUploadOptions = lawFirmCaseUploadOptions;

export const mapOrderField = (field) => {
  const mapping = {
    assigneeName: 'assignee.name',
    dueDate: 'case.dueDate',
    patientName: 'patient.name',
    status: 'case.status',
    study: 'study.description',
    workspaceName: 'workspace.name',
  };

  return mapping[field] || field;
};
export default class Case extends Base {
  id = 0;
  workspace = null;
  patient = null;
  patientWorkspace = {};
  originalAssignee = null;
  assignee = null;
  name = '';
  questionText = '';
  createdAt;
  status = caseStatuses.new;
  dueDate;
  reviewDate;
  description = '';
  title = '';
  writtenReport = '';
  reviewer = null;
  viewerLink = '';
  dicomStudy = null;
  pdfReport = null;
  ratingComment = '';
  ratingScore = 0;
  reports = [];
  reportType = null;
  videoReport = null;
  supplementaryMaterials = [];
  additionalFiles = [];
  resource = {};

  constructor(source) {
    super();
    this.clone(source);
    this.dueDate = this.dueDate ? new Date(this.dueDate) : null;
    this.reviewDate = this.reviewDate ? new Date(this.reviewDate) : null;
    this.createdAt = this.createdAt ? new Date(this.createdAt) : null;
    if (this.status) {
      if (typeof this.status === 'string') {
        if (this.status === caseStatuses.new.value && this.isDelayed) {
          // Delayed status
          this.status = caseStatuses.delayed;
        } else {
          // Others
          this.status = caseStatuses[this.status];
        }
      }
    } else {
      this.status = caseStatuses.new;
    }
  }

  get isDelayed() {
    const dueDate = new Date(this.dueDate);
    const dateDiff = dueDate.getTime() - new Date().getTime();
    const diffInHours = dateDiff / 1000 / 60 / 60;

    return diffInHours < 24;
  }

  get statusColor() {
    if (this.status === caseStatuses.new) {
      return this.isDelayed ? caseColors.delayed : caseColors.new;
    }
    return caseColors[this.status.value];
  }

  get patientName() {
    return this.patient && this.patient.name;
  }

  get workspaceName() {
    if (this.originalWorkspace?.name) return this.originalWorkspace.name;
    if (this.patientWorkspace?.name) return this.patientWorkspace.name;
    return this.workspace && this.workspace.name;
  }

  get assigneeName() {
    return this.assignee && this.assignee.name;
  }

  get patientPreferredName() {
    return this.patient && this.patient.preferredName;
  }

  get patientDateOfBirth() {
    return this.patient && this.patient.dateOfBirth;
  }

  get reviewerName() {
    return this.reviewer && this.reviewer.name;
  }

  get studyFileType() {
    return this.dicomStudy?.type || 'dcm';
  }
}
